/**
 * Aircraft Service
 * Handles aircraft API operations
 */

import { api } from './api';
import type { Aircraft, AircraftListResponse, AircraftResponse, AircraftFilters } from '../types/aircraft';

/**
 * Get all aircraft with optional filters
 */
export const getAircraft = async (filters?: AircraftFilters): Promise<Aircraft[]> => {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.isActive !== undefined) {
      params.append('isActive', String(filters.isActive));
    }
    if (filters.aircraftType) {
      params.append('aircraftType', filters.aircraftType);
    }
    if (filters.search) {
      params.append('search', filters.search);
    }
  }

  const queryString = params.toString();
  const endpoint = `/aircraft${queryString ? `?${queryString}` : ''}`;

  const response = await api.get<AircraftListResponse>(endpoint);
  return response.data;
};

/**
 * Get active aircraft only
 */
export const getActiveAircraft = async (): Promise<Aircraft[]> => {
  return getAircraft({ isActive: true });
};

/**
 * Get single aircraft by ID
 */
export const getAircraftById = async (id: number): Promise<Aircraft> => {
  const response = await api.get<AircraftResponse>(`/aircraft/${id}`);
  return response.data;
};

/**
 * Create new aircraft
 */
export const createAircraft = async (aircraftData: {
  tailNumber: string;
  aircraftType: string;
  crewSize?: number;
  year?: number;
}): Promise<Aircraft> => {
  const response = await api.post<AircraftResponse>('/aircraft', aircraftData);
  return response.data;
};

/**
 * Update aircraft
 */
export const updateAircraft = async (
  id: number,
  updates: Partial<{
    aircraftType: string;
    crewSize: number;
    year: number;
    isActive: boolean;
  }>
): Promise<Aircraft> => {
  const response = await api.put<AircraftResponse>(`/aircraft/${id}`, updates);
  return response.data;
};

/**
 * Delete aircraft
 */
export const deleteAircraft = async (id: number): Promise<void> => {
  await api.delete(`/aircraft/${id}`);
};

/**
 * Get aircraft formatted for form options
 */
export const getAircraftOptions = async (): Promise<Array<{ value: string; label: string }>> => {
  const aircraft = await getActiveAircraft();
  return aircraft.map(a => ({
    value: a.tailNumber,
    label: `${a.tailNumber} - ${a.aircraftType}`
  }));
};

export default {
  getAircraft,
  getActiveAircraft,
  getAircraftById,
  createAircraft,
  updateAircraft,
  deleteAircraft,
  getAircraftOptions,
};